import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ThumbsUp, ExternalLink, Link2, Plus, Gift, ShoppingBag, Home, Heart, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link } from "react-router-dom";

interface ProductLink {
  id: string;
  link_url: string;
  link_title: string | null;
  message: string;
  sender_name: string | null;
  sender_email: string;
  votes: string[];
  vote_count: number;
}

interface DiscussionIdeasProps {
  entityType: "event" | "purchase";
  entityId: string;
  userEmail: string;
  userName: string;
  occasion?: string | null;
  themeColor: string;
}

// Occasion-based category mappings
const OCCASION_CATEGORIES: Record<string, { name: string; icon: any; color: string }[]> = {
  birthday: [
    { name: "Personalized Gifts", icon: Gift, color: "bg-pink-500" },
    { name: "Experiences", icon: Heart, color: "bg-rose-500" },
    { name: "Gadgets", icon: Sparkles, color: "bg-purple-500" },
    { name: "Fashion", icon: ShoppingBag, color: "bg-indigo-500" },
  ],
  wedding: [
    { name: "Home & Living", icon: Home, color: "bg-amber-500" },
    { name: "Kitchen & Dining", icon: Heart, color: "bg-orange-500" },
    { name: "Experiences", icon: Sparkles, color: "bg-rose-500" },
    { name: "Personalized", icon: Gift, color: "bg-pink-500" },
  ],
  baby_shower: [
    { name: "Baby Essentials", icon: Heart, color: "bg-sky-500" },
    { name: "Nursery", icon: Home, color: "bg-teal-500" },
    { name: "Toys & Books", icon: Gift, color: "bg-violet-500" },
    { name: "Keepsakes", icon: Sparkles, color: "bg-pink-500" },
  ],
  housewarming: [
    { name: "Home Decor", icon: Home, color: "bg-emerald-500" },
    { name: "Kitchen", icon: Heart, color: "bg-amber-500" },
    { name: "Plants & Garden", icon: Sparkles, color: "bg-green-500" },
    { name: "Smart Home", icon: ShoppingBag, color: "bg-blue-500" },
  ],
  default: [
    { name: "Electronics", icon: Sparkles, color: "bg-blue-500" },
    { name: "Home & Living", icon: Home, color: "bg-amber-500" },
    { name: "Fashion", icon: ShoppingBag, color: "bg-purple-500" },
    { name: "Experiences", icon: Heart, color: "bg-rose-500" },
  ],
};

export function DiscussionIdeas({
  entityType,
  entityId,
  userEmail,
  userName,
  occasion,
  themeColor,
}: DiscussionIdeasProps) {
  const [productLinks, setProductLinks] = useState<ProductLink[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Get categories based on occasion
  const categories = OCCASION_CATEGORIES[occasion || 'default'] || OCCASION_CATEGORIES.default;

  const tableName = entityType === "event" ? "event_discussions" : "group_purchase_discussions";
  const foreignKey = entityType === "event" ? "reminder_id" : "purchase_id";

  useEffect(() => {
    fetchProductLinks();
  }, [entityId]);

  const fetchProductLinks = async () => {
    let data: any[] = [];
    let error: any = null;
    
    if (entityType === "event") {
      const result = await supabase
        .from("event_discussions")
        .select("*")
        .eq("reminder_id", entityId)
        .eq("message_type", "product_link")
        .order("vote_count", { ascending: false });
      data = result.data || [];
      error = result.error;
    } else {
      const result = await supabase
        .from("group_purchase_discussions")
        .select("*")
        .eq("purchase_id", entityId)
        .eq("message_type", "product_link")
        .order("vote_count", { ascending: false });
      data = result.data || [];
      error = result.error;
    }

    if (!error && data) {
      setProductLinks(data.map((d: any) => ({
        id: d.id,
        link_url: d.link_url,
        link_title: d.link_title,
        message: d.message,
        sender_name: d.sender_name,
        sender_email: d.sender_email,
        votes: Array.isArray(d.votes) ? d.votes : [],
        vote_count: d.vote_count || 0,
      })));
    }
  };

  const handleAddLink = async () => {
    if (!linkUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setSubmitting(true);
    try {
      let error: any = null;
      
      if (entityType === "event") {
        const result = await supabase
          .from("event_discussions")
          .insert({
            reminder_id: entityId,
            sender_email: userEmail,
            sender_name: userName,
            message: linkTitle.trim() || "Shared a gift idea",
            message_type: "product_link",
            link_url: linkUrl.trim(),
            link_title: linkTitle.trim() || null,
          });
        error = result.error;
      } else {
        const result = await supabase
          .from("group_purchase_discussions")
          .insert({
            purchase_id: entityId,
            sender_email: userEmail,
            sender_name: userName,
            message: linkTitle.trim() || "Shared a gift idea",
            message_type: "product_link",
            link_url: linkUrl.trim(),
            link_title: linkTitle.trim() || null,
          });
        error = result.error;
      }

      if (error) throw error;
      
      setLinkUrl("");
      setLinkTitle("");
      setShowAddForm(false);
      fetchProductLinks();
      toast.success("Idea shared!");
    } catch (error) {
      console.error("Error adding link:", error);
      toast.error("Failed to share idea");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (linkId: string) => {
    const link = productLinks.find(l => l.id === linkId);
    if (!link) return;

    const hasVoted = link.votes.includes(userEmail);
    const newVotes = hasVoted 
      ? link.votes.filter(v => v !== userEmail)
      : [...link.votes, userEmail];

    try {
      const { error } = await supabase
        .from(tableName)
        .update({ 
          votes: newVotes,
          vote_count: newVotes.length 
        })
        .eq("id", linkId);

      if (error) throw error;
      fetchProductLinks();
    } catch (error) {
      console.error("Error voting:", error);
    }
  };

  const getThemeClasses = () => {
    switch (themeColor) {
      case "pink":
        return {
          button: "bg-pink-600 hover:bg-pink-700",
          accent: "text-pink-600",
          border: "border-pink-200 dark:border-pink-900/30",
          bg: "bg-pink-500/5",
        };
      case "emerald":
        return {
          button: "bg-emerald-600 hover:bg-emerald-700",
          accent: "text-emerald-600",
          border: "border-emerald-200 dark:border-emerald-900/30",
          bg: "bg-emerald-500/5",
        };
      default:
        return {
          button: "bg-purple-600 hover:bg-purple-700",
          accent: "text-purple-600",
          border: "border-purple-200 dark:border-purple-900/30",
          bg: "bg-purple-500/5",
        };
    }
  };

  const theme = getThemeClasses();

  return (
    <div className="space-y-4">
      {/* Add Idea Button */}
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-sm text-muted-foreground">
          Shared by the Group ({productLinks.length})
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddForm(!showAddForm)}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Share Idea
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className={theme.border}>
          <CardContent className="p-4 space-y-3">
            <Input
              placeholder="Paste product URL *"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
            <Input
              placeholder="Title or description (optional)"
              value={linkTitle}
              onChange={(e) => setLinkTitle(e.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddLink} disabled={submitting} className={theme.button}>
                {submitting ? "Sharing..." : "Share"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product Links as Colored Tiles */}
      <div>
        {productLinks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Link2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No ideas shared yet</p>
            <p className="text-xs mt-1">Be the first to suggest a gift!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {productLinks.map((link, index) => {
              const hasVoted = link.votes.includes(userEmail);
              const tileColors = [
                "bg-gradient-to-br from-pink-400 to-rose-500",
                "bg-gradient-to-br from-purple-400 to-indigo-500",
                "bg-gradient-to-br from-amber-400 to-orange-500",
                "bg-gradient-to-br from-emerald-400 to-teal-500",
                "bg-gradient-to-br from-sky-400 to-blue-500",
                "bg-gradient-to-br from-fuchsia-400 to-pink-500",
              ];
              const colorClass = tileColors[index % tileColors.length];
              
              return (
                <div 
                  key={link.id} 
                  className={`relative rounded-xl overflow-hidden cursor-pointer group ${hasVoted ? 'ring-2 ring-offset-2 ring-pink-500' : ''}`}
                  onClick={() => handleVote(link.id)}
                >
                  <div className={`${colorClass} p-4 min-h-[120px] flex flex-col justify-between`}>
                    <div>
                      <a 
                        href={link.link_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-white font-medium text-sm hover:underline line-clamp-2 flex items-start gap-1"
                      >
                        {link.link_title || link.message}
                        <ExternalLink className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      </a>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-white/80 text-xs truncate max-w-[60%]">
                        by {link.sender_name || link.sender_email.split('@')[0]}
                      </p>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${hasVoted ? 'bg-white text-pink-600' : 'bg-white/20 text-white'}`}>
                        <ThumbsUp className={`w-3 h-3 ${hasVoted ? 'fill-current' : ''}`} />
                        <span className="text-xs font-medium">{link.vote_count}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Browse by Category - For Events and Purchases */}
      {/* Recommendations as Colored Tiles */}
      {(entityType === "event" || entityType === "purchase") && (
        <div className="pt-4 border-t">
          <h3 className="font-medium text-sm text-muted-foreground mb-3">
            Recommendations for {occasion ? occasion.replace(/_/g, ' ') : 'you'}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {categories.map((cat) => (
              <Link 
                key={cat.name}
                to="/gallery"
                onClick={() => window.scrollTo(0, 0)}
                className={`${cat.color} rounded-xl p-4 text-white hover:opacity-90 transition-opacity flex flex-col items-center justify-center min-h-[80px] text-center`}
              >
                <cat.icon className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
